import type {
  GenerationDetail,
  GenerationExample,
  GenerationItem,
  GenerationMarks,
  GenerationPick,
  GenerationSenseStatus,
} from '../model/types';

/**
 * Разрез раскрытой записи «Недавно добавленных» (ТЗ главной § 22.5) — только данные: разделы по виду
 * элемента и строки значений по группам `meanings[].pos` от сервера. Подписи частей речи и помет,
 * раскладку и порции делает виджет (сущность фичу не импортирует). Чистая функция без alias-импортов.
 */

export type BreakdownSectionKind = 'translations' | 'words' | 'phrases' | 'phrasalVerbs' | 'skipped';

/** Строка значения: выбранный перевод, пометы, пример с переводом и смысл. Подсказки в записи нет. */
export interface BreakdownSense {
  /** Ключ строки — стабилен между перечитками состава. */
  key: string;
  /** Часть речи группы сервера (каноническая), иначе — из выбора. */
  pos: string | null;
  /** Только выбранный перевод (решение владельца 2026-10-06). */
  translation: string;
  status: GenerationSenseStatus;
  marks: GenerationMarks;
  /** Простое предложение с переводом; нет пары — живой пример без перевода. */
  example: GenerationExample | null;
  definition: string | null;
}

export interface BreakdownItem {
  item: GenerationItem;
  /** Строки значений. Пусто, когда у элемента только строка элемента (не получилось, опечатка, удалена…). */
  senses: BreakdownSense[];
  /** Переводы одной строкой — «что» в строке элемента. */
  translationsLine: string;
  /** Недобравшиеся значения — их отмечает «Ввести снова». */
  failedPicks: GenerationPick[];
}

export interface BreakdownSection {
  kind: BreakdownSectionKind;
  /** Число для подписи — сколько в разделе карточек (у «Переводов» не пишется). */
  count: number;
  items: BreakdownItem[];
}

export interface Breakdown {
  /** Транскрипция — только у записи слова. */
  transcription: string | null;
  sections: BreakdownSection[];
}

const SECTION_ORDER: BreakdownSectionKind[] = ['translations', 'words', 'phrases', 'phrasalVerbs', 'skipped'];

/** Элемент с готовыми или идущими значениями — строки значений; остальные — строка элемента. */
const ROWS_STATUSES = new Set<GenerationItem['status']>(['ready', 'generating', 'extending']);

const norm = (text: string) => text.trim().toLowerCase();
const pickKey = (pos: string | null, translation: string) => `${norm(pos ?? '')}|${norm(translation)}`;

function pickExample(simple: GenerationExample | null, live: GenerationExample | null): GenerationExample | null {
  if (simple?.en && simple.ru) return { en: simple.en, ru: simple.ru };
  if (live?.en) return { en: live.en, ru: null };
  if (simple?.en) return { en: simple.en, ru: null };
  return null;
}

function sensesOf(item: GenerationItem): BreakdownSense[] {
  const rows: BreakdownSense[] = [];
  const seen = new Set<string>();
  for (const meaning of item.meanings) {
    for (const sense of meaning.senses) {
      const pos = meaning.pos ?? sense.pick.pos;
      const key = pickKey(pos, sense.pick.translation);
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        key: `${item.id}:${key}`,
        pos,
        translation: sense.pick.translation,
        status: sense.status,
        marks: sense.marks,
        example: pickExample(sense.simpleExample, sense.example),
        definition: sense.definition,
      });
    }
  }
  // Недобравшиеся значения, которых сервер не вернул строкой значения, — строкой «не добавилось».
  if (item.status === 'ready') {
    for (const pick of item.failedPicks) {
      const key = pickKey(pick.pos, pick.translation);
      if (rows.some((row) => pickKey(row.pos, row.translation) === key || norm(row.translation) === norm(pick.translation)))
        continue;
      rows.push({
        key: `${item.id}:${key}`,
        pos: pick.pos,
        translation: pick.translation,
        status: 'failed',
        marks: { registers: [], region: [], grammar: [], usageNote: null, frequency: null, cefr: null },
        example: null,
        definition: null,
      });
    }
  }
  return rows;
}

function translationsLine(item: GenerationItem): string {
  const out: string[] = [];
  const add = (text: string) => {
    const value = text.trim();
    if (value && !out.some((x) => norm(x) === norm(value))) out.push(value);
  };
  for (const meaning of item.meanings) for (const sense of meaning.senses) add(sense.pick.translation);
  if (out.length === 0) for (const pick of item.failedPicks) add(pick.translation);
  return out.join(', ');
}

function failedPicksOf(item: GenerationItem, senses: BreakdownSense[]): GenerationPick[] {
  if (item.failedPicks.length) return item.failedPicks;
  if (item.status === 'selection_failed') {
    return item.meanings.flatMap((m) => m.senses.map((s) => ({ pos: s.pick.pos ?? m.pos, translation: s.pick.translation })));
  }
  return senses.filter((s) => s.status === 'failed').map((s) => ({ pos: s.pos, translation: s.translation }));
}

function sectionOf(item: GenerationItem, recordKind: GenerationDetail['kind']): BreakdownSectionKind {
  if (item.status === 'skipped') return 'skipped';
  if (item.kind === 'phrase') return 'phrases';
  if (item.kind === 'phrasal_verb') return 'phrasalVerbs';
  return recordKind === 'topic' ? 'words' : 'translations';
}

export function buildBreakdown(detail: GenerationDetail): Breakdown {
  const groups = new Map<BreakdownSectionKind, BreakdownItem[]>();
  for (const item of detail.items) {
    const kind = sectionOf(item, detail.kind);
    const senses = ROWS_STATUSES.has(item.status) ? sensesOf(item) : [];
    const entry: BreakdownItem = {
      item,
      senses,
      translationsLine: translationsLine(item),
      failedPicks: item.status === 'ready' || item.status === 'selection_failed' ? failedPicksOf(item, senses) : [],
    };
    const list = groups.get(kind) ?? [];
    list.push(entry);
    groups.set(kind, list);
  }
  const word = detail.kind === 'word' ? detail.items.find((item) => item.kind === 'word' && item.transcription) : undefined;
  return {
    transcription: word?.transcription ?? null,
    sections: SECTION_ORDER.filter((kind) => groups.has(kind)).map((kind) => {
      const items = groups.get(kind)!;
      return { kind, count: items.length, items };
    }),
  };
}

/** Слово, по которому искать для «Ввести снова» (ТЗ § 22.6): у фраз и фразовых глаголов записи слова —
 *  заголовок записи (фразы живут в статье главного слова), у остальных — слово элемента. */
export function reenterWord(detail: Pick<GenerationDetail, 'kind' | 'title'>, item: GenerationItem): string {
  if (detail.kind === 'word' && item.kind !== 'word' && detail.title.trim()) return detail.title.trim();
  return item.frontName;
}
